import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  IUseTablePreserverDecoderConfigItem,
  TUseTablePreserverDecoderConfigs,
} from '../interfaces';
import {
  getTablePreserverDecoderCommonConfig,
  tablePreserverDefaultParser,
  tablePreserverDefaultIsValid,
} from '../helpers';

type TUseTablePreserverDecoderReturn<TParams> = {
  [key in keyof TParams]: any;
}

const useTablePreserverDecoder = <TParams>(
  searchParams: ReturnType<typeof useSearchParams>[0],
  configs: TUseTablePreserverDecoderConfigs<TParams>,
): TUseTablePreserverDecoderReturn<TParams> => {
  const isInitialized = useRef(false);

  if (isInitialized.current) {
    return {} as TUseTablePreserverDecoderReturn<TParams>;
  }

  isInitialized.current = true;

  return Object.entries(configs).reduce<TUseTablePreserverDecoderReturn<TParams>>((
    acc,
    [name, configOrFlag],
  ) => {
    let config;
    if (typeof configOrFlag === 'boolean') {
      if (!configOrFlag) {
        return acc;
      }
      config = getTablePreserverDecoderCommonConfig(name);
    } else {
      config = configOrFlag;
    }
    const {
      fallbackValue = undefined,
      parser = tablePreserverDefaultParser,
      isValid = tablePreserverDefaultIsValid,
    } = config as IUseTablePreserverDecoderConfigItem;
    try {
      const value = searchParams.get(name as string);
      if (value === null || value === '') { // missing value
        acc[name as keyof TParams] = fallbackValue;
        return acc;
      }
      const parsedValue = parser(value);
      acc[name as keyof TParams] = isValid(parsedValue) ? parsedValue : fallbackValue;
      return acc;
    } catch (e) {
      acc[name as keyof TParams] = fallbackValue;
      return acc;
    }
  }, {} as TUseTablePreserverDecoderReturn<TParams>);
};

export default useTablePreserverDecoder;
